import React from 'react'
import { makeStyles } from '@material-ui/core'
import { formatNumberWithAlphaMask } from 'phone-numbers'
import classNames from 'classnames'
import Spinner from 'spinner'
import { lighten, darken } from '@material-ui/core/styles/colorManipulator'
import { Typography, Icon } from 'foundations-library/components'
import { formatPrice } from '../../util/formatters'

/**
 * Data representing an individual phone number
 */
export interface PhoneNumberData {
    e164: string,
    price?: number, // price is an integer number of cents, currency is assumed to be USD.
    mask?: string, // eslint-disable-line @typescript-eslint/naming-convention
}

interface PhoneNumberProps {
    testId?: string;
    phoneNumber: PhoneNumberData,
    isRecommended?: boolean;
    isSmallView?: boolean;
    onClick?: () => void;
    numberBeingSelected?: PhoneNumberData | null;
    isNumberTaken: boolean;
}

const useStyles = makeStyles(theme => {
    const { colors } = theme['foundations-library'] || {}

    return {
        box: {
            display: 'flex',
            width: '210px',
            height: '76px',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '8px !important',
            padding: '24px 0 !important',
            gap: '8px',
            border: 0,
            cursor: 'pointer',
            position: 'relative',
            backgroundColor: ({ isBeingSelected, isRecommended }) => isBeingSelected && !isRecommended ? colors?.primary[50] : undefined,
            [theme.breakpoints.down('sm')]: {
                margin: '0 auto'
            },
            '& .call-icon-container': {
                width: '28px !important',
                height: '28px !important',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '50% !important',
                '&.dark-grey-bg': {
                    backgroundColor: colors?.neutral[900]
                },
                '&.light-grey-bg': {
                    backgroundColor: colors?.neutral[50]
                },
                '& .call-icon': {
                    '&.white-color path': {
                        fill: '#fff'
                    },
                    '&.dark-grey-color path': {
                        fill: colors?.neutral[500]
                    }
                }
            },
            '&:focus': {
                outline: 'none'
            },
            '&:disabled': {
                cursor: 'not-allowed',
                opacity: 0.5
            },
            '& .neutral-900-color': {
                color: colors?.neutral[900]
            },

            /* clear out global css getting applied from marketing-website */
            fontSize: 'unset !important',
            fontWeight: 'unset !important',
            lineHeight: 'unset !important',
            letterSpacing: 'unset !important',
            color: 'unset !important'
        },
        numberTaken: {
            backgroundColor: colors?.destructive[100],
            boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.16)',
            '&:hover': {
                backgroundColor: colors?.destructive[50]
            }
        },
        shadowed: {
            backgroundColor: 'transparent',
            boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.16)',
            '&:hover': {
                backgroundColor: colors?.neutral[50]
            }
        },
        centralArea: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',

            width: '208px',
            height: '28px'
        },
        spinner: {
            width: '24px !important',
            height: '24px !important',
            color: `${colors?.primary[700]}`
        },
        reservingRecommendNumberText: {
            color: 'white'
        },
        numberText: {
            fontSize: props => props.isSmallView ? '20px' : '16px',
            display: 'flex',
            alignItems: 'center'
        },
        letters: {
            fontWeight: 600
        },
        priceTag: {
            borderRadius: '2px !important',
            color: darken(theme.palette.primary.main, 0.2),
            backgroundColor: lighten(theme.palette.primary.main, 0.85),
            zIndex: 1,
            position: 'absolute',
            top: '2px',
            right: '2px',
            padding: '0px 10px !important'
        },
        highlighted: {
            backgroundColor: colors?.primary[200],
            color: colors?.neutral[900],
            '&:hover': {
                backgroundColor: colors?.primary[300]
            }
        },
        highlightedPriceTag: {
            color: 'white',
            backgroundColor: theme.palette.primary.textDark
        },
        priceTagContainer: {
            position: 'absolute',
            top: '2px',
            right: '2px',
            display: 'flex',
            padding: '0 7px !important',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '10px !important',
            background: colors?.primary[50]
        },
        priceTagText: {
            color: colors?.primary[600]
        }
    }
})

// Customized X icon with a transclucent background
const RemoveIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="24" rx="12" fill="#FF7A6B" fillOpacity="0.3"/>
        <path d="M16.2879 8.61856C16.405 8.5014 16.405 8.31145 16.2879 8.1943L15.8057 7.71213C15.6885 7.59497 15.4986 7.59497 15.3814 7.71213L12 11.0936L8.61856 7.71213C8.5014 7.59497 8.31145 7.59497 8.1943 7.71213L7.71213 8.1943C7.59497 8.31145 7.59497 8.5014 7.71213 8.61856L11.0936 12L7.71213 15.3814C7.59497 15.4986 7.59497 15.6885 7.71213 15.8057L8.1943 16.2879C8.31145 16.405 8.5014 16.405 8.61856 16.2879L12 12.9064L15.3814 16.2879C15.4986 16.405 15.6885 16.405 15.8057 16.2879L16.2879 15.8057C16.405 15.6885 16.405 15.4986 16.2879 15.3814L12.9064 12L16.2879 8.61856Z" fill="#E85646"/>
    </svg>
)

/**
 * Displays an individual number that the user can pick
 */
const PhoneNumber = ({ testId, phoneNumber, isRecommended, isSmallView, onClick, numberBeingSelected = null, isNumberTaken }: PhoneNumberProps): JSX.Element => {
    const disabled = numberBeingSelected !== null
    const isBeingSelected = numberBeingSelected?.e164 === phoneNumber.e164

    const classes = useStyles({ isSmallView, disabled, isBeingSelected })

    const formattedNumber = formatNumberWithAlphaMask(phoneNumber.e164, phoneNumber.mask || '.'.repeat(phoneNumber.e164.length), { useAreaCodeBrackets: true })

    // Renders the phone number so the letters are rendered bold/strong
    const renderPhoneNumber = (formattedNumber: string) => {
        if (!/[A-Z]/.test(formattedNumber)) {
            const num = formattedNumber
            const [area, rest] = num.split(')')
            return (
                <>
                    {renderSubtitleVariantText(`${area})`)}
                    <>&nbsp;</>
                    {renderBodyVariantText(`${rest.trim()}`)}
                </>
            )
        }

        const chars = formattedNumber.replace(/[\W_]+/g, '') // keep only alphanumeric characters
        const segments = [`(${chars.substring(0, 3)})`, <>&nbsp;</>]
        let word = chars[3]
        for (let i = 4; i < chars.length; i++) {
            if (/[A-Z]/.test(chars[i]) === /[A-Z]/.test(chars[i - 1])) {
                word += chars[i] // previous char and current char are both letters or both non-letters
            } else {
                segments.push(word)
                segments.push('-')
                word = chars[i]
            }
        }
        if (word) {
            segments.push(word)
        }

        return (
            <>
                {segments.map((seg, idx) => typeof seg === 'string'
                    ? (
                        seg.match(/[A-Z]/) || seg.match(/\)/) ? renderSubtitleVariantText(seg) : renderBodyVariantText(seg)
                    )
                    : seg)}
            </>
        )
    }

    const renderCallIcon = () => {
        return (
            <div className={`call-icon-container ${isRecommended ? 'dark-grey-bg' : 'light-grey-bg'}`}>
                <Icon size={'small'} name='call' className={`call-icon ${isRecommended ? 'white-color' : 'dark-grey-color'}`} />
            </div>
        )
    }

    const renderSubtitleVariantText = (text: string) => {
        return (
            <Typography variant="subtitle1" className={'neutral-900-color'}>{text}</Typography>
        )
    }

    const renderBodyVariantText = (text: string) => {
        return (
            <Typography variant="body1" className={'neutral-900-color'}>{text}</Typography>
        )
    }

    const renderPriceTag = (price: string) => {
        return (
            <div className={classes.priceTagContainer}>
                <Typography variant="captionHeavy" className={classes.priceTagText}>{price}</Typography>
            </div>
        )
    }

    const renderLoading = () => {
        return (
            <>
                <Spinner className={classes.spinner} />
                <Typography variant='body2' color='primary-700' remoteConfigID="phone_number_selecting" />
            </>
        )
    }

    const boxClasses = classNames(classes.box, {
        [`${classes.highlighted}`]: isRecommended, // recommended number
        [`${classes.shadowed}`]: !isRecommended, // general number
        [`${classes.numberTaken}`]: isNumberTaken // number taken
    })

    return (
        <button data-testid={testId} onClick={onClick} className={boxClasses} disabled={disabled}>
            {isBeingSelected
                ? renderLoading()
                : (
                    <>
                        {(phoneNumber.price && !isNumberTaken) ? renderPriceTag(phoneNumber.price ? formatPrice(phoneNumber.price, '$', { alwaysShowCents: false, useCommas: true }) : 'FREE') : null}
                        <>
                            {isNumberTaken && <RemoveIcon />}
                            {renderCallIcon()}
                            <div style={{ display: 'flex' }}>
                                {renderPhoneNumber(formattedNumber)}
                            </div>
                        </>
                    </>
                )}
        </button>
    )
}

export default PhoneNumber
